/* ==========================================================================
   Video Reels — lógica externa (WPO)
   Antes este script viajaba en línea dentro de sections/video-reels.liquid,
   por lo que se volvía a parsear en cada carga y bloqueaba el hilo principal
   durante el parseo del HTML. Ahora es un asset con defer: se descarga en
   paralelo, se cachea y se ejecuta después de armar el DOM.

   Mejoras de rendimiento respecto de la versión anterior:
     1. El lightbox se construye una sola vez por página (antes: uno por
        instancia de sección) y se pre-arma en tiempo ocioso cuando la sección
        se acerca al viewport. Antes se construía dentro del handler de click:
        ese trabajo sincrónico era una de las causas directas del INP alto.
     2. Un solo listener de click delegado sobre el track, en lugar de uno por
        tarjeta.
     3. Sin "layout thrashing": el ancho de tarjeta y el gap se miden una vez
        y se cachean; se recalculan con ResizeObserver, nunca dentro de un
        handler de click.
     4. Los handlers de scroll se agrupan con requestAnimationFrame y los de
        scroll/touch son { passive: true }.
     5. El resize usa debounce de 150 ms cuando no hay ResizeObserver.
     6. El video se pausa al ocultar la pestaña y al cerrar el lightbox, y se
        carga con preload="none": no se descarga nada hasta que el usuario
        abre un reel.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_SELECTOR = '.video-reels[data-section-id]';
  var RESIZE_DEBOUNCE = 150;
  var OBSERVER_MARGIN = '200px 0px';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

  /* Ejecuta una tarea en tiempo ocioso del navegador (sin robarle prioridad
     a la interacción del usuario). Si el navegador no soporta
     requestIdleCallback, cae a un setTimeout corto. */
  function runWhenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 200);
    }
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  /* Busca el ancestro que coincide con el selector. Reemplazo simple de
     Element.closest para navegadores viejos. */
  function closestMatch(el, selector, boundary) {
    while (el && el !== boundary && el.nodeType === 1) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  /* ========================================================================
     LIGHTBOX (singleton compartido por todas las instancias de la sección)
     ======================================================================== */

  var LB = {
    root: null,
    built: false,
    cards: [],
    index: 0
  };

  var escapeNode = null;

  function escapeHtml(str) {
    if (!escapeNode) escapeNode = document.createElement('div');
    escapeNode.textContent = str == null ? '' : String(str);
    return escapeNode.innerHTML;
  }

  var LIGHTBOX_MARKUP = [
    '<button type="button" class="vr-lightbox__close" aria-label="Cerrar">',
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
        '<line x1="18" y1="6" x2="6" y2="18"></line>',
        '<line x1="6" y1="6" x2="18" y2="18"></line>',
      '</svg>',
    '</button>',
    '<div class="vr-lightbox__inner">',
      '<div class="vr-lightbox__stage">',
        '<div class="vr-lightbox__video-wrapper">',
          '<div class="vr-lightbox__progress" data-progress></div>',
          '<div class="vr-lightbox__controls">',
            '<button type="button" class="vr-lightbox__ctrl-btn" data-play-toggle aria-label="Reproducir o pausar">',
              '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14"></rect><rect x="14" y="5" width="4" height="14"></rect></svg>',
            '</button>',
            '<button type="button" class="vr-lightbox__ctrl-btn" data-sound-toggle aria-label="Activar o silenciar el sonido">',
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
                '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor"></polygon>',
                '<line x1="23" y1="9" x2="17" y2="15"></line>',
                '<line x1="17" y1="9" x2="23" y2="15"></line>',
              '</svg>',
            '</button>',
          '</div>',
          '<video class="vr-lightbox__video" playsinline preload="none" loop muted></video>',
          '<button type="button" class="vr-lightbox__tap-play" aria-label="Reproducir">',
            '<span class="vr-lightbox__tap-play-icon">',
              '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
            '</span>',
          '</button>',
          '<div class="vr-lightbox__nav">',
            '<button type="button" class="vr-lightbox__nav-btn" data-nav="-1" aria-label="Video anterior">',
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
                '<polyline points="18 15 12 9 6 15"></polyline>',
              '</svg>',
            '</button>',
            '<button type="button" class="vr-lightbox__nav-btn" data-nav="1" aria-label="Video siguiente">',
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
                '<polyline points="6 9 12 15 18 9"></polyline>',
              '</svg>',
            '</button>',
          '</div>',
          '<button type="button" class="vr-lightbox__cart-btn" data-cart-btn aria-label="Ver todos los productos">',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
              '<circle cx="9" cy="21" r="1"></circle>',
              '<circle cx="20" cy="21" r="1"></circle>',
              '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>',
            '</svg>',
            '<span class="vr-lightbox__cart-count" data-cart-count>0</span>',
          '</button>',
          '<a class="vr-lightbox__main-product" data-main-product href="#">',
            '<img class="vr-lightbox__main-product-img" data-main-img alt="" loading="lazy" decoding="async" width="44" height="44">',
            '<div class="vr-lightbox__main-product-info">',
              '<p class="vr-lightbox__main-product-name" data-main-name></p>',
              '<div class="vr-lightbox__main-product-prices">',
                '<span class="vr-lightbox__main-product-price" data-main-price></span>',
                '<span class="vr-lightbox__main-product-compare" data-main-compare></span>',
              '</div>',
            '</div>',
          '</a>',
          '<div class="vr-sheet-backdrop" data-sheet-backdrop></div>',
          '<div class="vr-sheet" data-sheet>',
            '<div class="vr-sheet__handle"><div class="vr-sheet__handle-bar"></div></div>',
            '<div class="vr-sheet__header">',
              '<h3 class="vr-sheet__title" data-sheet-title>Productos del video</h3>',
              '<button type="button" class="vr-sheet__close" data-sheet-close aria-label="Cerrar">',
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
                  '<line x1="18" y1="6" x2="6" y2="18"></line>',
                  '<line x1="6" y1="6" x2="18" y2="18"></line>',
                '</svg>',
              '</button>',
            '</div>',
            '<div class="vr-sheet__list" data-sheet-list></div>',
          '</div>',
        '</div>',
      '</div>',
    '</div>'
  ].join('');

  function buildLightbox() {
    if (LB.built) return;
    LB.built = true;

    var root = document.createElement('div');
    root.className = 'vr-lightbox';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML = LIGHTBOX_MARKUP;
    document.body.appendChild(root);

    LB.root = root;
    LB.video = root.querySelector('.vr-lightbox__video');
    LB.wrapper = root.querySelector('.vr-lightbox__video-wrapper');
    LB.prevBtn = root.querySelector('[data-nav="-1"]');
    LB.nextBtn = root.querySelector('[data-nav="1"]');
    LB.playBtn = root.querySelector('[data-play-toggle]');
    LB.soundBtn = root.querySelector('[data-sound-toggle]');
    LB.progress = root.querySelector('[data-progress]');

    LB.mainProduct = root.querySelector('[data-main-product]');
    LB.mainImg = root.querySelector('[data-main-img]');
    LB.mainName = root.querySelector('[data-main-name]');
    LB.mainPrice = root.querySelector('[data-main-price]');
    LB.mainCompare = root.querySelector('[data-main-compare]');

    LB.cartBtn = root.querySelector('[data-cart-btn]');
    LB.cartCount = root.querySelector('[data-cart-count]');
    LB.sheet = root.querySelector('[data-sheet]');
    LB.sheetBackdrop = root.querySelector('[data-sheet-backdrop]');
    LB.sheetList = root.querySelector('[data-sheet-list]');
    LB.sheetTitle = root.querySelector('[data-sheet-title]');
    LB.sheetClose = root.querySelector('[data-sheet-close]');
    LB.tapPlay = root.querySelector('.vr-lightbox__tap-play');

    root.querySelector('.vr-lightbox__close').addEventListener('click', closeLightbox);
    LB.prevBtn.addEventListener('click', function (e) { e.stopPropagation(); go(-1); });
    LB.nextBtn.addEventListener('click', function (e) { e.stopPropagation(); go(1); });
    LB.playBtn.addEventListener('click', function (e) { e.stopPropagation(); togglePlay(); });
    LB.soundBtn.addEventListener('click', function (e) { e.stopPropagation(); toggleSound(); });
    LB.cartBtn.addEventListener('click', function (e) { e.stopPropagation(); openSheet(); });
    LB.sheetClose.addEventListener('click', function (e) { e.stopPropagation(); closeSheet(); });
    LB.sheetBackdrop.addEventListener('click', function (e) { e.stopPropagation(); closeSheet(); });
    LB.tapPlay.addEventListener('click', function (e) { e.stopPropagation(); togglePlay(); });

    root.addEventListener('click', function (e) {
      if (
        e.target === root ||
        e.target.classList.contains('vr-lightbox__inner') ||
        e.target.classList.contains('vr-lightbox__stage')
      ) {
        closeLightbox();
      }
    });

    LB.video.addEventListener('timeupdate', updateProgress);
    LB.video.addEventListener('play', function () {
      LB.wrapper.classList.remove('is-paused');
      updatePlayIcon(true);
    });
    LB.video.addEventListener('pause', function () {
      LB.wrapper.classList.add('is-paused');
      updatePlayIcon(false);
    });

    document.addEventListener('keydown', onKeydown);

    /* Si el usuario cambia de pestaña, pausamos: reproducir un video que nadie
       ve es trabajo de hilo principal (y de red) desperdiciado. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && LB.video && !LB.video.paused) LB.video.pause();
    });

    var touchStartY = null;
    var touchStartX = null;

    LB.wrapper.addEventListener('touchstart', function (e) {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    LB.wrapper.addEventListener('touchend', function (e) {
      if (touchStartY === null) return;
      var dy = e.changedTouches[0].clientY - touchStartY;
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx)) {
        if (dy < 0) go(1);
        else go(-1);
      }
      touchStartY = null;
      touchStartX = null;
    }, { passive: true });
  }

  function onKeydown(e) {
    if (!LB.root || !LB.root.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      if (LB.sheet.classList.contains('is-open')) closeSheet();
      else closeLightbox();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      go(1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      go(-1);
    } else if (e.key === ' ') {
      e.preventDefault();
      togglePlay();
    }
  }

  function updatePlayIcon(playing) {
    var svg = LB.playBtn.querySelector('svg');
    if (playing) {
      svg.innerHTML = '<rect x="6" y="5" width="4" height="14"></rect><rect x="14" y="5" width="4" height="14"></rect>';
    } else {
      svg.innerHTML = '<path d="M8 5v14l11-7z"/>';
    }
  }

  function updateSoundIcon(muted) {
    var svg = LB.soundBtn.querySelector('svg');
    if (muted) {
      svg.innerHTML = '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line>';
    } else {
      svg.innerHTML = '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path><path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>';
    }
  }

  function renderProgress() {
    var frag = document.createDocumentFragment();
    for (var i = 0; i < LB.cards.length; i++) {
      var bar = document.createElement('div');
      bar.className = 'vr-lightbox__progress-bar';
      if (i < LB.index) bar.className += ' is-done';
      if (i === LB.index) {
        var fill = document.createElement('div');
        fill.className = 'vr-lightbox__progress-fill';
        fill.setAttribute('data-current-fill', '');
        bar.appendChild(fill);
      }
      frag.appendChild(bar);
    }
    LB.progress.innerHTML = '';
    LB.progress.appendChild(frag);
    LB.progressFill = LB.progress.querySelector('[data-current-fill]');
  }

  function updateProgress() {
    var fill = LB.progressFill;
    if (!fill || !LB.video.duration) return;
    fill.style.width = (LB.video.currentTime / LB.video.duration) * 100 + '%';
  }

  function loadVideo(index) {
    LB.index = index;

    var card = LB.cards[index];
    if (!card) return;

    var videoUrl = card.getAttribute('data-video-url');
    var posterUrl = card.getAttribute('data-poster-url');

    LB.video.pause();
    LB.video.removeAttribute('src');

    if (posterUrl) LB.video.setAttribute('poster', posterUrl);
    else LB.video.removeAttribute('poster');

    if (videoUrl) {
      LB.video.setAttribute('src', videoUrl);
      LB.video.load();
      var playPromise = LB.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          LB.wrapper.classList.add('is-paused');
        });
      }
    }

    renderProducts(card);
    renderProgress();

    LB.prevBtn.disabled = index === 0;
    LB.nextBtn.disabled = index === LB.cards.length - 1;
  }

  function renderProducts(card) {
    var data;
    try {
      data = JSON.parse(card.getAttribute('data-products'));
    } catch (err) {
      data = [];
    }

    // Cerrar el sheet si quedó abierto al cambiar de video
    closeSheet();

    if (!data.length) {
      LB.mainProduct.style.display = 'none';
      LB.cartBtn.classList.remove('is-visible');
      LB.wrapper.classList.remove('has-cart-btn');
      return;
    }

    // Producto principal (el primero de la lista)
    var main = data[0];
    LB.mainProduct.style.display = 'flex';
    LB.mainProduct.href = main.url;
    LB.mainImg.src = main.image;
    LB.mainImg.alt = main.title;
    LB.mainName.textContent = main.title;
    LB.mainPrice.textContent = main.price;

    if (main.on_sale) {
      LB.mainCompare.textContent = main.compare;
      LB.mainCompare.style.display = '';
    } else {
      LB.mainCompare.style.display = 'none';
    }

    // Botón de carrito + bottom sheet (solo si hay más de un producto)
    if (data.length > 1) {
      LB.cartBtn.classList.add('is-visible');
      LB.wrapper.classList.add('has-cart-btn');
      LB.cartCount.textContent = data.length;
      LB.sheetTitle.textContent = 'Productos del video (' + data.length + ')';

      var html = '';
      for (var i = 0; i < data.length; i++) {
        var p = data[i];
        var compareHtml = p.on_sale
          ? '<span class="vr-sheet__item-compare">' + escapeHtml(p.compare) + '</span>'
          : '';
        html +=
          '<a href="' + escapeHtml(p.url) + '" class="vr-sheet__item">' +
            '<img src="' + escapeHtml(p.image) + '" alt="" class="vr-sheet__item-img" loading="lazy" decoding="async" width="56" height="56">' +
            '<div class="vr-sheet__item-info">' +
              '<p class="vr-sheet__item-name">' + escapeHtml(p.title) + '</p>' +
              '<div class="vr-sheet__item-prices">' +
                '<span class="vr-sheet__item-price">' + escapeHtml(p.price) + '</span>' +
                compareHtml +
              '</div>' +
            '</div>' +
          '</a>';
      }
      // Una sola escritura en el DOM en lugar de N appendChild
      LB.sheetList.innerHTML = html;
    } else {
      LB.cartBtn.classList.remove('is-visible');
      LB.wrapper.classList.remove('has-cart-btn');
    }
  }

  function openSheet() {
    if (!LB.built) return;
    LB.sheetBackdrop.classList.add('is-open');
    requestAnimationFrame(function () {
      LB.sheet.classList.add('is-open');
    });
  }

  function closeSheet() {
    if (!LB.built) return;
    LB.sheet.classList.remove('is-open');
    LB.sheetBackdrop.classList.remove('is-open');
  }

  function go(direction) {
    var next = LB.index + direction;
    if (next < 0 || next >= LB.cards.length) return;
    loadVideo(next);
  }

  function togglePlay() {
    if (!LB.video) return;
    if (LB.video.paused) LB.video.play();
    else LB.video.pause();
  }

  function toggleSound() {
    LB.video.muted = !LB.video.muted;
    updateSoundIcon(LB.video.muted);
  }

  function openLightbox(cards, index) {
    buildLightbox();
    LB.cards = cards;
    document.body.classList.add('vr-lightbox-open');
    requestAnimationFrame(function () {
      LB.root.classList.add('is-open');
    });
    LB.video.muted = true;
    updateSoundIcon(true);
    loadVideo(index);
  }

  function closeLightbox() {
    if (!LB.built) return;
    LB.video.pause();
    LB.video.removeAttribute('src');
    LB.video.load();
    closeSheet();
    LB.root.classList.remove('is-open');
    document.body.classList.remove('vr-lightbox-open');
  }

  /* ========================================================================
     CARRUSEL — una instancia por sección
     ======================================================================== */

  function initSection(section) {
    if (!section || section.getAttribute('data-vr-init') === '1') return;
    section.setAttribute('data-vr-init', '1');

    var track = section.querySelector('[data-track]');
    if (!track) return;

    var prevBtn = section.querySelector('[data-track-nav="-1"]');
    var nextBtn = section.querySelector('[data-track-nav="1"]');
    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-card]'));
    if (!cards.length) return;

    /* Medidas cacheadas: el paso de scroll se calcula una sola vez y se
       refresca solo cuando cambia el tamaño. Así el handler de click nunca
       fuerza un reflow (causa clásica de INP alto). */
    var step = 0;

    function measure() {
      var rect = cards[0].getBoundingClientRect();
      if (!rect.width) return; // la sección todavía está oculta / sin layout
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      step = rect.width + gap;
    }

    var navFrame = 0;

    function updateNavState() {
      navFrame = 0;
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 5;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= maxScroll - 5;
    }

    /* Agrupamos las lecturas de layout en un único frame: aunque el evento
       scroll se dispare decenas de veces, medimos como máximo una vez por
       cuadro de animación. */
    function scheduleNavState() {
      if (navFrame) return;
      navFrame = requestAnimationFrame(updateNavState);
    }

    function scrollByCard(direction) {
      if (!step) measure();
      if (!step) return;
      track.scrollBy({ left: step * direction, behavior: scrollBehavior });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () { scrollByCard(-1); });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () { scrollByCard(1); });
    }

    track.addEventListener('scroll', scheduleNavState, { passive: true });

    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () {
        measure();
        scheduleNavState();
      });
      ro.observe(track);
    } else {
      window.addEventListener('resize', debounce(function () {
        measure();
        scheduleNavState();
      }, RESIZE_DEBOUNCE), { passive: true });
    }

    /* Un único listener delegado para las N tarjetas. */
    track.addEventListener('click', function (e) {
      var card = closestMatch(e.target, '[data-card]', track);
      if (!card) return;
      e.preventDefault();
      openLightbox(cards, cards.indexOf(card));
    });

    measure();
    scheduleNavState();

    /* Pre-armado del lightbox: cuando la sección se acerca al viewport
       (200 px antes), construimos su DOM en tiempo ocioso. De este modo el
       primer click solo asigna datos y reproduce, en vez de generar todo el
       markup dentro del handler. */
    if (typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(function (entries, observer) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            observer.disconnect();
            runWhenIdle(buildLightbox);
            break;
          }
        }
      }, { rootMargin: OBSERVER_MARGIN });
      io.observe(section);
    } else {
      runWhenIdle(buildLightbox);
    }
  }

  function initAll(scope) {
    var root = scope || document;
    var sections = root.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* ===== Soporte del editor de temas ===== */

  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target;
    if (target && target.matches && target.matches(SECTION_SELECTOR)) initSection(target);
    else if (target) initAll(target);
  });

  document.addEventListener('shopify:section:select', function (e) {
    var target = e.target;
    if (!target) return;
    if (target.matches && target.matches(SECTION_SELECTOR)) initSection(target);
    else initAll(target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var target = e.target;
    if (!target || !target.querySelector) return;
    if (target.querySelector(SECTION_SELECTOR) || (target.matches && target.matches(SECTION_SELECTOR))) {
      closeLightbox();
    }
  });

  document.addEventListener('shopify:section:deselect', function () {
    closeLightbox();
  });
})();
